import {
    PARTNER_COCKPIT_REIMBURSEMENT_REASONS,
    type PartnerCockpitReimbursementReason,
} from '@houzy/schemas/partner-cockpit/reimbursement';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useReimbursementEligibility } from '../api/reimbursementApi';
import { ReimbursementStepActions } from '../components/ReimbursementStepActions';
import { reimbursementReasonLabelKey, reimbursementReasonUnavailableKey } from '../labels';
import { useReimbursementDraft } from '../state/reimbursementDraftContext';

export function ReasonStep(): ReactElement {
    const { t } = useTranslation();
    const { draft, updateDraft, offerRequestLeadUuid } = useReimbursementDraft();
    const eligibility = useReimbursementEligibility(offerRequestLeadUuid);
    const availableReasons = eligibility.data?.availableReasons ?? [];

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Typography variant="h5" component="h2">
                    {t('REIMBURSEMENT.REASON.TITLE')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t('REIMBURSEMENT.REASON.DESCRIPTION')}
                </Typography>
            </Stack>

            <RadioGroup
                aria-label={t('REIMBURSEMENT.REASON.TITLE')}
                value={draft.reason ?? ''}
                onChange={(event) => updateDraft({ reason: event.target.value as PartnerCockpitReimbursementReason })}>
                <Stack spacing={1.5}>
                    {PARTNER_COCKPIT_REIMBURSEMENT_REASONS.map((reason) => {
                        const isAvailable = availableReasons.includes(reason);
                        return (
                            <Card key={reason} variant="outlined">
                                <CardActionArea
                                    disabled={!isAvailable}
                                    onClick={() => updateDraft({ reason })}
                                    sx={{ px: 2, py: 1 }}>
                                    <FormControlLabel
                                        value={reason}
                                        disabled={!isAvailable}
                                        control={<Radio checked={draft.reason === reason} />}
                                        label={t(reimbursementReasonLabelKey(reason))}
                                        sx={{ pointerEvents: 'none', width: '100%' }}
                                    />
                                    {!isAvailable && (
                                        <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
                                            {t(reimbursementReasonUnavailableKey(reason))}
                                        </Typography>
                                    )}
                                </CardActionArea>
                            </Card>
                        );
                    })}
                </Stack>
            </RadioGroup>

            <ReimbursementStepActions currentStep="reason" nextDisabled={draft.reason === undefined} />
        </Stack>
    );
}
